<template lang="pug">
component(
    :is="transition"
    v-bind="animationClasses"
    @enter="emitOpened"
    @leave="emitClosed"
)
    div(
        class="fixed z-50 inset-0 overflow-hidden lg:overflow-y-scroll"
        :aria-labelledby="title"
        role="dialog"
        aria-modal="true"
    )
        div(class="relative flex items-center justify-center h-full min-h-full lg:h-auto")
            div(
                class="absolute w-full h-full bg-black opacity-80"
                @click="close"
            )
            div(
                class="relative flex flex-col w-full h-full lg:rounded lg:my-8 lg:h-auto"
                :class="[modalSizeClasses, !isCustom && enableDarkMode ? 'bg-gray-lighter lg:bg-white' : 'bg-white' ]"
            )
                header(
                    v-if="title || !!$slots['header']"
                    class="flex-none min-h-[40px] flex justify-between items-center px-6 py-3 text-xl font-bold border-b border-gray-light bg-white uppercase lg:rounded-t lg:text-dark lg:normal-case lg:text-lg lg:bg-gray-lighter lg:border-b-0"
                )
                    slot(name="header")
                        div(v-if="title" class="title m-0")
                            | {{ title }}

                    div(class="lg:absolute lg:left-full lg:top-0 lg:ml-2")
                        xIcon(
                            aria-label="close"
                            @click="close"
                            class="w-4 h-4 cursor-pointer lg:fill-current lg:stroke-current lg:text-white"
                            data-testid="overlayClose"
                        )

                template(v-else)
                    div(class="absolute top-0 right-0 p-4 lg:left-full lg:right-auto lg:p-0 lg:ml-2")
                        xIcon(
                            aria-label="close"
                            @click="close"
                            class="w-4 h-4 cursor-pointer lg:fill-current lg:stroke-current lg:text-white"
                        )

                div(
                    class="overflow-y-auto lg:overflow-y-visible"
                    :class="{ 'p-5': !this.isCustom }"
                )
                    slot

                footer(
                    v-if="!!$slots['footer']"
                    class="flex-none mt-auto border-t border-gray-light bg-white lg:bg-transparent lg:border-t-0"
                    :class="{ 'p-5': !this.isCustom }"
                )
                    slot(name="footer")

</template>

<script>
// We need to add Appear if the component is V-IF and remove it if V-SHOW
import xIcon from '@/assets/common/x?inline'
import TransitionIf from './transition-if'
import TransitionShow from './transition-show'

export default {
    name: 'Overlay',
    components: {
        TransitionIf,
        TransitionShow,
        xIcon,
    },
    props: {
        // String to place in the Header
        title: {
            type: String,
            default: null,
        },
        /*
            uniqueKey is used by $overlay to open/close the overlay
            and as the string for ?mod if the overlay is registered.
        */
        uniqueKey: {
            type: String,
            default: null,
        },
        // Direction for panel to slide in from
        slideDirection: {
            type: String,
            default: 'left',
            validator: (value) => {
                return value === 'left' || value === 'right'
            },
        },
        // Sets content of panel to gray. Use when there is a form.
        enableDarkMode: {
            type: Boolean,
            default: false,
        },
        // Custom is for modals that need more design control, so we remove some css defaults
        isCustom: {
            type: Boolean,
            default: false,
        },
        modalSize: {
            type: String,
            default: 'normal',
            validator: (value) => {
                return (
                    value === 'small' || value === 'normal' || value === 'wide' || value === 'full'
                )
            },
        },
    },
    data() {
        return {
            // We need to swap the transition markup depending on if we are V-IF and V-SHOW
            transition: 'TransitionIf',
        }
    },
    head() {
        return {
            htmlAttrs: {
                style: this.activeOverlay ? 'overflow: hidden' : '',
            },
        }
    },
    computed: {
        activeOverlay() {
            return this.$overlay.state.activeOverlay
        },
        animationClasses() {
            const translateSlideClass =
                this.slideDirection === 'left' ? '-translate-x-full' : 'translate-x-full'
            return {
                'enter-class': `transform ${translateSlideClass} lg:opacity-0`,
                'enter-active-class':
                    'transition-transform lg:transform-none lg:transition-opacity duration-300',
                'enter-to-class': 'transform translate-x-0',
                'leave-class': 'transform translate-x-0',
                'leave-active-class':
                    'transition-transform lg:transform-none lg:transition-opacity duration-300',
                'leave-to-class': `transform ${translateSlideClass} lg:opacity-0`,
            }
        },
        modalSizeClasses() {
            return {
                'lg:w-[400px]': this.modalSize === 'small',
                'lg:w-[640px]': this.modalSize === 'normal',
                'lg:w-[700px]': this.modalSize === 'wide',
            }
        },
    },
    created() {
        // Detect if we are V-SHOW: If this overlay is created but not active we need to use the TransitionShow component
        if (this.uniqueKey !== this.activeOverlay) this.transition = 'TransitionShow'
    },
    methods: {
        close() {
            this.$emit('close')
        },
        emitClosed() {
            // removeQuery only needs to be called if we are on a small device (mobile or tablet) and if we have a uniqueKey
            if (this.$device.isDesktop || !this.uniqueKey) return
            this.$overlay.removeQuery(this.$route)
        },
        emitOpened() {
            // addQuery only needs to be called if we are on a small device (mobile or tablet) and if we have a uniqueKey
            if (this.$device.isDesktop || !this.uniqueKey) return
            this.$overlay.addQuery(this.uniqueKey, this.$route)
        },
    },
}
</script>
